import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  scan,
  shareReplay,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
  throwError
} from 'rxjs';
import { startWith } from 'rxjs/operators';
import { BaseDomainModel } from '../../../models/base/base-domain-model';
import { UserDomainModel } from '../../user-domain-model';
import { WebSocketService } from '../../../services/web-socket.service';
import { WebSocketMessageContextEvent } from '../../../models/web-sockets/contexts/web-socket-message-context-event';
import { WebSocketMessageContextError } from '../../../models/web-sockets/contexts/web-socket-message-context-error';
import { WebSocketAssetTranscodeStatusUpdate } from '../../../models/web-sockets/messages/receive/web-socket-asset-transcode-status-update';
import { WebSocketAssetUploaded } from '../../../models/web-sockets/messages/receive/web-socket-asset-uploaded';
import { WebSocketAssetDeleted } from '../../../models/web-sockets/messages/receive/web-socket-asset-deleted';
import { WebSocketDuplicateAssetError } from '../../../models/web-sockets/messages/receive/web-socket-duplicate-asset-error';
import { MediaManagerAssetLibraryDto } from '../../../models/image/dto/media-manager-asset-library-dto';
import { exists } from '../../../functions/exists';
import { MediaManagerDebugLogger } from '../debug';
import { createEmptyMultiUploadState, reduceMultiUploadState } from '../upload';
import {
  MediaManagerFileSystem,
  MediaManagerFolderTreeNode
} from 'src/app/models/media-manager/media-manager-file-system';
import { mediaManagerSpecialTopLevelFolderIds } from 'src/app/models/media-manager/media-manager-special-folder';
import {
  MediaManagerMultiUploadEvent,
  MediaManagerMultiUploadState
} from 'src/app/models/media-manager/media-manager-multi-upload-state';
import { MediaConvertJobStatus } from 'src/app/models/enum/dto/media-convert-job-status.enum';
import { AssetRepository } from '../../../repositories/asset/asset-repository';

export type MediaManagerLibraryKey = number;

export type MediaManagerAssetDeleteStatus =
  | 'delete-pending'
  | 'delete-confirmed'
  | 'delete-in-progress'
  | 'delete-completed'
  | 'delete-failed';

export interface MediaManagerAssetDeleteState {
  assetUniqueId: string;
  batchId: string;
  status: MediaManagerAssetDeleteStatus;
  errorMessage: string | null;
}

export interface MediaManagerFilesystemChange {
  version: number;
  reason?: string;
}

export interface MediaManagerLibraryContext {
  brandId: string;
  libraryKey: MediaManagerLibraryKey;
  filesystem: MediaManagerFileSystem;
  assetLibrary: MediaManagerAssetLibraryDto;
}

/**
 * State-only backbone for the Media Manager. Owns the asset library, filesystem projection, multi-upload
 * event stream, delete-state maps, websocket message shares, success-toast bus, and the debug logger.
 *
 * The store depends only on lower-level services (ImageApi, WebSocketService, CompanyDomainModel,
 * UserDomainModel). Every media-manager feature/sync service depends on the store, and MediaManagerDomainModel
 * composes the store into its public API surface. This split breaks what would otherwise be a circular DI
 * graph between the domain model and its feature services.
 */
@Injectable() // Provided by Logged In Scope
export class MediaManagerStore extends BaseDomainModel {
  constructor(
    private userDomainModel: UserDomainModel,
    private webSocketService: WebSocketService,
    private assetRepository: AssetRepository
  ) {
    super();
    this.setupAssetLibrarySub();
    this.setupUnfilteredFileSystemFolderTreeSub();
  }

  public readonly debug = new MediaManagerDebugLogger();

  // --- Success toast bus -----------------------------------------------------

  private readonly _successToasts = new Subject<string>();
  public readonly successToasts$ = this._successToasts as Observable<string>;
  connectToSuccessToasts = (msg: string) => this._successToasts.next(msg);
  public publishMediaManagerSuccessMessage(message: string): void {
    this.connectToSuccessToasts(message);
  }

  // --- WebSocket message shares ---------------------------------------------

  public readonly assetTranscodeUpdateMessages$ = this.webSocketService
    .getRelevantMessage$<WebSocketAssetTranscodeStatusUpdate>(WebSocketMessageContextEvent.AssetTranscodeStatusUpdate)
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));

  public readonly assetUploadedMessages$ = this.webSocketService
    .getRelevantMessage$<WebSocketAssetUploaded>(WebSocketMessageContextEvent.AssetUploaded)
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));

  public readonly assetDeletedMessages$ = this.webSocketService
    .getRelevantMessage$<WebSocketAssetDeleted>(WebSocketMessageContextEvent.AssetDeleted)
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));

  public readonly duplicateAssetMessages$ = this.webSocketService
    .getRelevantMessage$<WebSocketDuplicateAssetError>(WebSocketMessageContextError.DuplicateAssetExists)
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));

  // --- Asset library ---------------------------------------------------------

  private _activeBrandId = new BehaviorSubject<MediaManagerLibraryKey | null>(null);
  public activeBrandId$ = this._activeBrandId as Observable<MediaManagerLibraryKey | null>;
  public connectToActiveBrandId(activeBrandId: MediaManagerLibraryKey | null) {
    this._activeBrandId.next(activeBrandId);
  }
  private activeBrandIdValue: MediaManagerLibraryKey | null = null;

  public getActiveBrandIdValue(): MediaManagerLibraryKey | null {
    return this.activeBrandIdValue;
  }

  private _assetLibrariesByBrandId = new BehaviorSubject<Map<MediaManagerLibraryKey, MediaManagerAssetLibraryDto>>(
    new Map<MediaManagerLibraryKey, MediaManagerAssetLibraryDto>()
  );
  public assetLibrariesByBrandId$ = this._assetLibrariesByBrandId as Observable<
    Map<MediaManagerLibraryKey, MediaManagerAssetLibraryDto>
  >;
  public connectToAssetLibrariesByBrandId(
    assetLibrariesByBrandId: Map<MediaManagerLibraryKey, MediaManagerAssetLibraryDto>
  ) {
    this._assetLibrariesByBrandId.next(assetLibrariesByBrandId);
  }
  private assetLibrariesByBrandIdValue = new Map<MediaManagerLibraryKey, MediaManagerAssetLibraryDto>();
  private libraryKeysByBrandId = new Map<string, MediaManagerLibraryKey>();
  private brandIdsByLibraryKey = new Map<MediaManagerLibraryKey, string>();

  public assetLibrary$: Observable<MediaManagerAssetLibraryDto | null> = combineLatest([
    this.activeBrandId$,
    this.assetLibrariesByBrandId$
  ]).pipe(
    map(([brandId, assetLibrariesByBrandId]) => (brandId ? (assetLibrariesByBrandId.get(brandId) ?? null) : null))
  );

  // --- Filesystem projection -------------------------------------------------

  private _filesystemsByBrandId = new BehaviorSubject<Map<MediaManagerLibraryKey, MediaManagerFileSystem>>(
    new Map<MediaManagerLibraryKey, MediaManagerFileSystem>()
  );
  public filesystemsByBrandId$ = this._filesystemsByBrandId as Observable<
    Map<MediaManagerLibraryKey, MediaManagerFileSystem>
  >;
  public connectToFilesystemsByBrandId(filesystemsByBrandId: Map<MediaManagerLibraryKey, MediaManagerFileSystem>) {
    this._filesystemsByBrandId.next(filesystemsByBrandId);
  }
  private filesystemsByBrandIdValue = new Map<MediaManagerLibraryKey, MediaManagerFileSystem>();

  public filesystem$: Observable<MediaManagerFileSystem | null> = combineLatest([
    this.activeBrandId$,
    this.filesystemsByBrandId$
  ]).pipe(map(([brandId, filesystemsByBrandId]) => (brandId ? (filesystemsByBrandId.get(brandId) ?? null) : null)));
  public filesystemReady$ = this.filesystem$.pipe(
    map(filesystem => exists(filesystem)),
    distinctUntilChanged()
  );

  // TODO: Alex - Confirm syncPOSAssets not needed (delete relevant calls etc)
  // public readonly syncPOSAssets$ = this.companyDomainModel.companyConfiguration$.pipe(
  //   map(companyConfiguration => companyConfiguration?.syncPOSAssets === true),
  //   distinctUntilChanged()
  // );
  public readonly syncPOSAssets$ = of(false);

  private readonly activeSpecialTopLevelFolderIds$: Observable<ReadonlySet<string>> = this.syncPOSAssets$.pipe(
    // Add future top-level special folder suppression rules here.
    map(syncPOSAssets => (syncPOSAssets ? mediaManagerSpecialTopLevelFolderIds : new Set<string>())),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  private _filesystemChange = new BehaviorSubject<MediaManagerFilesystemChange>({ version: 0 });
  public filesystemChange$ = this._filesystemChange as Observable<MediaManagerFilesystemChange>;

  private _unfilteredFilesystemFolderTree = new BehaviorSubject<MediaManagerFolderTreeNode | null>(null);
  public unfilteredFilesystemFolderTree$ = this
    ._unfilteredFilesystemFolderTree as Observable<MediaManagerFolderTreeNode | null>;
  public connectToUnfilteredFilesystemFolderTree(fsFolderTree: MediaManagerFolderTreeNode) {
    this._unfilteredFilesystemFolderTree.next(fsFolderTree);
  }

  /** Snapshot the current filesystem projection without going through the observable. */
  public getFilesystemValue() {
    const activeBrandId = this.activeBrandIdValue;
    if (!activeBrandId) return null;
    return this.filesystemsByBrandIdValue.get(activeBrandId) ?? null;
  }

  /** Snapshot the current asset library without going through the observable. */
  public getAssetLibraryValue() {
    const activeBrandId = this.activeBrandIdValue;
    if (!activeBrandId) return null;
    return this.assetLibrariesByBrandIdValue.get(activeBrandId) ?? null;
  }

  public notifyFilesystemChanged(reason?: string): void {
    this.filesystemChange$.pipe(take(1)).subscribe(currentChange => {
      this._filesystemChange.next({
        version: currentChange.version + 1,
        reason
      });
    });
  }

  // --- Multi-upload event stream --------------------------------------------

  public _multiUploadEvents = new Subject<MediaManagerMultiUploadEvent>();
  public multiUploadEvents$ = this._multiUploadEvents as Observable<MediaManagerMultiUploadEvent>;
  public connectToMultiUploadEvent(event: MediaManagerMultiUploadEvent): void {
    this._multiUploadEvents.next(event);
  }

  public multiFileUploadState$: Observable<MediaManagerMultiUploadState> = this.multiUploadEvents$.pipe(
    scan((state, event) => reduceMultiUploadState(state, event), createEmptyMultiUploadState()),
    startWith(createEmptyMultiUploadState()),
    // exception to the rule, we want this to emit a complete notification to shareReplay(1), so that it doesn't leak
    // eslint-disable-next-line rxjs/no-unsafe-takeuntil
    takeUntil(this.onDestroy),
    shareReplay(1)
  );

  // --- Asset delete states ---------------------------------------------------

  public _assetDeleteStates = new BehaviorSubject<Map<string, MediaManagerAssetDeleteState>>(
    new Map<string, MediaManagerAssetDeleteState>()
  );
  public assetDeleteStates$ = this._assetDeleteStates as Observable<Map<string, MediaManagerAssetDeleteState>>;
  /** Read the current asset-delete-state map without going through the observable. */
  public getAssetDeleteStatesValue(): Map<string, MediaManagerAssetDeleteState> {
    return this._assetDeleteStates.value;
  }
  /** Push a new asset-delete-state map for feature services to update per-batch delete progress. */
  public connectToAssetDeleteStates(deleteStates: Map<string, MediaManagerAssetDeleteState>): void {
    this._assetDeleteStates.next(deleteStates);
  }

  // --- Locally deleting folder ids ------------------------------------------

  public _locallyDeletingFolderIds = new BehaviorSubject<Set<string>>(new Set<string>());
  public locallyDeletingFolderIds$ = this._locallyDeletingFolderIds as Observable<Set<string>>;
  public connectToLocallyDeletingFolderIds(folderIds: Set<string>): void {
    this._locallyDeletingFolderIds.next(folderIds);
  }

  // --- Transcode progress ----------------------------------------------------

  /**
   * Live transcode progress (0-100) keyed by the asset's md5Hash, sourced directly from websocket transcode status
   * updates (metadata.MD5Hash matches the placeholder asset's md5Hash from the AssetUploaded event). Keying by
   * md5Hash instead of in-session upload state means progress survives modal backgrounding and full page reloads:
   * a user can land on a card mid-transcode and the bar picks up at the current percent on the next status update,
   * carrying through until completion.
   *
   * Progress is monotonic and never removed here: a completed job is pinned at 100 rather than deleted, so a card
   * can never regress to a lower/fallback percent in the brief window before the refetched, fully-processed asset
   * replaces the placeholder. The overlay itself disappears once the asset's outputProcessed flips true, not because
   * this map loses the entry.
   */
  public transcodeProgressByMd5Hash$ = this.assetTranscodeUpdateMessages$.pipe(
    map(message => message?.payload),
    filter(update => exists(update)),
    scan((progressByMd5Hash, update) => {
      const md5Hash = update?.getTranscodeMetadataValue('MD5Hash');
      if (!md5Hash) return progressByMd5Hash;
      const updatedProgressByMd5Hash = new Map(progressByMd5Hash);
      const previousPercent = updatedProgressByMd5Hash.get(md5Hash) ?? 0;
      const nextPercent = update.status === MediaConvertJobStatus.Complete ? 100 : (update.jobPercentComplete ?? 0);
      // Never let a late/out-of-order update move progress backwards for this job.
      updatedProgressByMd5Hash.set(md5Hash, Math.max(previousPercent, nextPercent));
      return updatedProgressByMd5Hash;
    }, new Map<string, number>()),
    startWith(new Map<string, number>()),
    // exception to the rule, we want this to emit a complete notification to shareReplay(1), so that it doesn't leak
    // eslint-disable-next-line rxjs/no-unsafe-takeuntil
    takeUntil(this.onDestroy),
    shareReplay(1)
  );

  // --- Asset library fetch ---------------------------------------------------

  public activateBrandLibrary$(brandId: string): Observable<MediaManagerAssetLibraryDto> {
    const normalizedBrandId = brandId?.trim();
    if (!normalizedBrandId) return throwError(() => new Error('A brand id is required to activate Media Manager.'));
    this.activeBrandIdValue = null;
    this.connectToActiveBrandId(null);
    this.notifyFilesystemChanged('active brand changed');
    return this.getBrandAssetLibrary$(normalizedBrandId).pipe(
      tap(assetLibrary => {
        const libraryKey = this.getLibraryKey(assetLibrary);
        this.activeBrandIdValue = libraryKey;
        this.connectToActiveBrandId(libraryKey);
        this.notifyFilesystemChanged('active brand changed');
        this.debugPrintActiveBrandFilesystem('active brand changed');
      })
    );
  }

  public getBrandAssetLibrary$(
    brandId: string,
    forceRefresh: boolean = false
  ): Observable<MediaManagerAssetLibraryDto> {
    const normalizedBrandId = brandId?.trim();
    if (!normalizedBrandId) return throwError(() => new Error('A brand id is required to fetch Media Manager.'));
    return this.assetLibrariesByBrandId$.pipe(
      take(1),
      switchMap(assetLibrariesByBrandId => {
        const libraryKey = this.libraryKeysByBrandId.get(normalizedBrandId);
        const cached = libraryKey ? assetLibrariesByBrandId.get(libraryKey) : null;
        if (cached && !forceRefresh) return of(cached);
        return this.assetRepository
          .getBrandAssetLibrary$(normalizedBrandId)
          .pipe(tap(assetLibrary => this.setBrandAssetLibrary(normalizedBrandId, assetLibrary)));
      })
    );
  }

  public getActiveLibraryContext$(): Observable<MediaManagerLibraryContext> {
    return this.activeBrandId$.pipe(
      take(1),
      switchMap(activeBrandId => {
        if (!activeBrandId) {
          return throwError(() => new Error('Media Manager has no active brand id.'));
        }
        return this.getBrandLibraryContext$(activeBrandId);
      })
    );
  }

  public getBrandLibraryContext$(brandId: MediaManagerLibraryKey | string): Observable<MediaManagerLibraryContext> {
    let libraryKey: MediaManagerLibraryKey | null | undefined;
    if (typeof brandId === 'number') {
      libraryKey = this.isValidLibraryKey(brandId) ? brandId : null;
    } else {
      libraryKey = this.libraryKeysByBrandId.get(brandId);
    }
    if (!libraryKey) {
      if (typeof brandId === 'string') {
        return this.getBrandAssetLibrary$(brandId).pipe(switchMap(() => this.getBrandLibraryContext$(brandId)));
      }
      return throwError(() => new Error(`MediaManager library is not initialized for brand '${brandId}'.`));
    }
    const requestBrandId = this.brandIdsByLibraryKey.get(libraryKey);
    if (!requestBrandId) {
      return throwError(() => new Error(`MediaManager has no request brand id for library '${libraryKey}'.`));
    }
    return combineLatest([this.assetLibrariesByBrandId$, this.filesystemsByBrandId$]).pipe(
      take(1),
      switchMap(([assetLibrariesByBrandId, filesystemsByBrandId]) => {
        const assetLibrary = assetLibrariesByBrandId.get(libraryKey);
        const filesystem = filesystemsByBrandId.get(libraryKey);
        if (!assetLibrary || !filesystem) {
          return throwError(() => new Error(`MediaManager filesystem is not initialized for brand '${libraryKey}'.`));
        }
        return of({ brandId: requestBrandId, libraryKey, filesystem, assetLibrary });
      })
    );
  }

  public refreshBrandAssetLibrary$(brandId: string | MediaManagerLibraryKey): Observable<MediaManagerAssetLibraryDto> {
    const requestBrandId = typeof brandId === 'number' ? this.brandIdsByLibraryKey.get(brandId) : brandId;
    if (!requestBrandId) {
      return throwError(() => new Error(`MediaManager has no request brand id for library '${brandId}'.`));
    }
    return this.getBrandAssetLibrary$(requestBrandId, true);
  }

  public getAssetLibrary$(): Observable<MediaManagerAssetLibraryDto> {
    return this.activeBrandId$.pipe(
      take(1),
      switchMap(activeBrandId => {
        if (!activeBrandId) {
          return throwError(() => new Error('Media Manager has no active brand id.'));
        }
        return this.refreshBrandAssetLibrary$(activeBrandId);
      })
    );
  }

  // --- Setup subs ------------------------------------------------------------

  private setupAssetLibrarySub() {
    const assetLibrarySub = combineLatest([
      this.activeBrandId$,
      this.assetLibrariesByBrandId$,
      this.userDomainModel.userSession$
    ])
      .pipe(
        debounceTime(1000),
        switchMap(([brandId, assetLibrariesByBrandId, session]) => {
          const hasAssetLibrary = brandId ? assetLibrariesByBrandId.has(brandId) : false;
          return brandId && !hasAssetLibrary && session?.validSession()
            ? this.refreshBrandAssetLibrary$(brandId)
            : of(brandId ? (assetLibrariesByBrandId.get(brandId) ?? null) : null);
        })
      )
      .subscribe();
    this.pushSub(assetLibrarySub);
  }

  private setupUnfilteredFileSystemFolderTreeSub() {
    const unfilteredFilesystemFolderTreeSub = this.filesystemChange$
      .pipe(
        tap(() => {
          const fs = this.getFilesystemValue();
          if (fs) {
            const folderTree = fs.generateTreeView();
            this.connectToUnfilteredFilesystemFolderTree(folderTree);
          }
        })
      )
      .subscribe();
    this.pushSub(unfilteredFilesystemFolderTreeSub);
  }

  private setBrandAssetLibrary(requestBrandId: string, assetLibrary: MediaManagerAssetLibraryDto): void {
    const libraryKey = this.getLibraryKey(assetLibrary);
    combineLatest([this.activeSpecialTopLevelFolderIds$, this.assetLibrariesByBrandId$, this.filesystemsByBrandId$])
      .pipe(take(1))
      .subscribe(([activeSpecialTopLevelFolderIds, currentAssetLibrariesByBrandId, currentFilesystemsByBrandId]) => {
        const assetLibrariesByBrandId = new Map(currentAssetLibrariesByBrandId);
        assetLibrariesByBrandId.set(libraryKey, assetLibrary);
        this.assetLibrariesByBrandIdValue = assetLibrariesByBrandId;
        this.libraryKeysByBrandId.set(requestBrandId, libraryKey);
        this.brandIdsByLibraryKey.set(libraryKey, requestBrandId);
        this.connectToAssetLibrariesByBrandId(assetLibrariesByBrandId);

        const filesystem = new MediaManagerFileSystem(assetLibrary, activeSpecialTopLevelFolderIds);
        const filesystemsByBrandId = new Map(currentFilesystemsByBrandId);
        filesystemsByBrandId.set(libraryKey, filesystem);
        this.filesystemsByBrandIdValue = filesystemsByBrandId;
        this.connectToFilesystemsByBrandId(filesystemsByBrandId);
        this.notifyFilesystemChanged('brand asset library rebuild');
        this.debugPrintActiveBrandFilesystem('brand asset library rebuild');
        this.debug.logFilesystemRebuilt(filesystem);
      });
  }

  private getLibraryKey(assetLibrary: MediaManagerAssetLibraryDto): MediaManagerLibraryKey {
    // All brand media libraries have system default top-level folders
    // whose companyId value is associated to the brandId in question.
    const libraryKey = assetLibrary?.assetFolders?.[0]?.companyId;
    if (!this.isValidLibraryKey(libraryKey)) {
      throw new Error('MediaManager asset library response is missing a folder company id.');
    }
    return libraryKey;
  }

  private isValidLibraryKey(libraryKey: number | null | undefined): libraryKey is MediaManagerLibraryKey {
    return typeof libraryKey === 'number' && Number.isFinite(libraryKey) && libraryKey > 0;
  }

  private debugPrintActiveBrandFilesystem(reason: string): void {
    if (!this.debug.shouldPrint()) return;
    const brandId = this.activeBrandIdValue;
    const filesystem = this.getFilesystemValue();
    if (!brandId || !filesystem) return;
    const cachedAssetLibraryCount = this.assetLibrariesByBrandIdValue.size;
    console.info(
      `---- MEDIA MANAGER FILESYSTEM TREE (${reason}) ----\n` +
        `BrandId: ${brandId}\n` +
        `Cached brand libraries: ${cachedAssetLibraryCount}\n` +
        filesystem.debugFormatFileSystemTree()
    );
  }
}
